// src/services/httpConfig-interceptors.js

/**
 * API Interceptors Configuration
 *
 * This file provides request and response interceptors for the API client.
 * Interceptors handle cross-cutting concerns like authentication, error handling,
 * and request/response transformations.
 *
 * Updated for Laravel 12 CSRF protection standards
 *
 * CRITICAL CHANGES:
 * - Removed duplicate timestamp addition (handled by axios config)
 * - Fixed 419 CSRF retry to use same axios instance
 * - Simplified response interceptor (JSON:API transform moved to wrapper)
 */

import { useEventBus } from '@vueuse/core';
import { authClient } from '@/httpConfig/config/axios.js';

const bus = useEventBus('auth:unauthorized');

/**
 * Sets up request interceptors for the API client
 *
 * @param {Object} axiosInstance - The Axios instance
 * @returns {Object} The configured interceptors
 */
export const setupRequestInterceptors = (axiosInstance) => {
  const requestInterceptor = (config) => {
    // Add timestamp to requests for cache busting
    // Only add if not already present
    if (!config.params?._t) {
      config.params = config.params || {};
      config.params._t = Date.now();
    }

    // Ensure credentials are included (should already be set in instance config)
    config.withCredentials = true;

    // Add CSRF token from cookie if available for Sanctum
    // Laravel 12 uses XSRF-TOKEN cookie and expects X-XSRF-TOKEN header
    const csrfToken = getCsrfTokenFromCookie();
    if (csrfToken) {
      config.headers = config.headers || {};
      config.headers['X-XSRF-TOKEN'] = csrfToken;
    }

    // Ensure X-Requested-With header (should already be set in instance config)
    config.headers = config.headers || {};
    config.headers['X-Requested-With'] = 'XMLHttpRequest';

    return config;
  };

  const requestErrorHandler = (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  };

  // Register the interceptors
  const interceptorId = axiosInstance.interceptors.request.use(
    requestInterceptor,
    requestErrorHandler,
  );

  return { requestInterceptor, requestErrorHandler, interceptorId };
};

/**
 * Sets up response interceptors for the API client
 *
 * @param {Object} axiosInstance - The Axios instance
 * @returns {Object} The configured interceptors
 */
export const setupResponseInterceptors = (axiosInstance) => {
  const responseInterceptor = (response) => {
    // Light processing - don't modify response structure here
    // JSON:API transformation is handled in the wrapper functions
    console.log('Response intercepted:', response.config.url, response.status);

    // Preserve meta and links in response object for easy access
    if (response.data?.meta) {
      response.meta = response.data.meta;
    }
    if (response.data?.links) {
      response.links = response.data.links;
    }

    return response;
  };

  const responseErrorHandler = async (error) => {
    // Store reference to original request config
    const originalRequest = error.config;
    console.log('Error occurred:', error);
    // Prevent infinite retry loops
    if (originalRequest._retry) {
      return Promise.reject(error);
    }

    if (error.response) {
      switch (error.response.status) {
        case 401: // Unauthorized
          console.warn('Unauthorized access - authentication required');
          bus.emit({ status: 401, error });
          break;

        case 403: // Forbidden
          console.warn('Permission denied:', error.response.data?.message);
          window.dispatchEvent(new CustomEvent('auth:forbidden', {
            detail: error.response.data,
          }));
          break;

        case 419: // CSRF token mismatch (Laravel specific)
          console.warn('CSRF token mismatch. Attempting to refresh token...');

          // Mark request to prevent infinite loops
          originalRequest._retry = true;

          try {
            // Refresh CSRF token using the SAME axios instance.
            //
            // baseURL override is load-bearing: the instance baseURL is
            // `${VITE_API_BASE_URL}/api/v1`, but Sanctum's CSRF endpoint is
            // mounted at the root (`/sanctum/csrf-cookie`), NOT under
            // `/api/v1`. Without this override, axios prepends `/api/v1`
            // and the request 404s — which then propagates as the user-
            // facing error masking the original 419. Using `VITE_API_BASE_URL
            // || ''` keeps tunnel mode (same-origin) working while honoring
            // an explicit base in modes that set one.
            await axiosInstance.get('/sanctum/csrf-cookie', {
              baseURL: import.meta.env.VITE_API_BASE_URL || '',
              withCredentials: true,
              // Prevent this request from being retried
              _skipRetry: true,
            });

            // Dispatch event for UI notification if needed
            window.dispatchEvent(new CustomEvent('auth:token-refreshed'));

            // Retry the original request with the SAME instance
            // The new CSRF token will be automatically included by the request interceptor
            return axiosInstance(originalRequest);
          } catch (refreshError) {
            console.error('Failed to refresh CSRF token:', refreshError);
            return Promise.reject(refreshError);
          }

        case 429: // Too Many Requests
          console.warn('Rate limit exceeded:', error.response.data?.message);
          window.dispatchEvent(new CustomEvent('api:rate-limited', {
            detail: error.response.data,
          }));
          break;

        case 500:
        case 502:
        case 503:
        case 504:
          console.error('Server error:', error.response.status, error.response.data?.message);
          window.dispatchEvent(new CustomEvent('api:server-error', {
            detail: {
              status: error.response.status,
              data: error.response.data,
            },
          }));
          break;

        default:
          if (error.response.status >= 400) {
            console.warn('API error:', error.response.status, error.response.data?.message);
          }
      }
    } else if (error.request) {
      // Request was made but no response received (network error)
      console.error('Network error - no response received:', error.message);
      window.dispatchEvent(new CustomEvent('api:network-error', {
        detail: error.message,
      }));
    } else {
      // Error in request configuration
      console.error('Request configuration error:', error.message);
    }

    return Promise.reject(error);
  };

  // Register the interceptors
  const interceptorId = axiosInstance.interceptors.response.use(
    responseInterceptor,
    responseErrorHandler,
  );

  return { responseInterceptor, responseErrorHandler, interceptorId };
};

/**
 * Gets the CSRF token from the XSRF-TOKEN cookie
 * Laravel 12 uses XSRF-TOKEN cookie (not CSRF-TOKEN)
 *
 * NOTE — duplicate-cookie hazard: when a device has cookies with the same
 * name but different Domain scopes (e.g. one set host-only `mac.poolit.club`
 * and another set wildcard `.poolit.club` after a session.php config change),
 * `document.cookie` returns BOTH. PHP's `$_COOKIE` parser keeps the LAST
 * value for duplicates, so we must do the same — otherwise the X-XSRF-TOKEN
 * header we send won't match the XSRF-TOKEN cookie Laravel reads, and any
 * state-changing request 419s. Match PHP's behavior by walking forward and
 * letting later occurrences overwrite earlier ones.
 *
 * @returns {string|null} The CSRF token or null if not found
 */
function getCsrfTokenFromCookie() {
  const cookies = document.cookie.split(';');
  let token = null;
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'XSRF-TOKEN' && value) {
      token = decodeURIComponent(value);
    }
  }
  return token;
}

/**
 * Manually fetch CSRF token (for initialization)
 * This should only be called once during app bootstrap if needed
 *
 * @returns {Promise<void>}
 */
export async function initializeCsrfToken(_axiosInstance) {
  try {
    // See the baseURL note in the 419-retry handler above — Sanctum's CSRF
    // endpoint lives at the root, NOT under `/api/v1`. Override the client's
    // baseURL so the request resolves to the correct path.
    await authClient.get('/sanctum/csrf-cookie', {
      baseURL: import.meta.env.VITE_API_BASE_URL || '',
      withCredentials: true,
    });
    console.log('CSRF token initialized');
  } catch (error) {
    console.error('Failed to initialize CSRF token:', error);
    throw error;
  }
}
